import type { ConnectorProvider, ConnectorSurface, ExclusionReason } from '@ralph/contracts'
import { PROVIDER_LABEL } from '@ralph/contracts'
import { pluralCategory } from '@/i18n/format.ts'
import type { Locale } from '@/i18n/locale.ts'

/**
 * `knowledgeFolders` — every word on the connected-folders surfaces (PRD-25).
 *
 * Extracted for the reason the connected-systems panel extracts its own: a sentence that
 * decides whether somebody understands what is about to land in a knowledge base, and who will
 * be able to search it, does not belong buried in JSX. Three rules run through all of it:
 *
 *  - **Name what did NOT happen.** "Nothing has been withdrawn", "nothing here changed",
 *    "nothing is lost" — the half of a message that calms a person is the half most products
 *    omit (D-18).
 *  - **No mechanism words.** Nothing here says delta, cursor, token, OAuth, chunk or embed.
 *    "Sign in", "check", "watching", "loaded", "left out" are the vocabulary.
 *  - **The quiet case is a sentence too.** "Checked 6m ago. Nothing had changed." is printed
 *    as prominently as a failure, because a connector that is silent when healthy is one
 *    nobody trusts by week three.
 *
 * English is the source of truth AND the type (PRD-27 LOC-011): `Messages = typeof en`, so
 * the Portuguese sibling is checked against the shape of this file and a missing key is a
 * compile error rather than a runtime fallback.
 *
 * ── Why so many entries begin with `fmt` ──
 *
 * Every number, size and date a sentence here prints goes through the ACTIVE locale's `Intl`
 * formatters (LOC-020), and this file is pure — no React — so the formatters arrive as a
 * parameter. Importing them bound at module scope would freeze the locale at first load and
 * then render the previous language with no type error, no runtime error and no failing test
 * (F-3), which is the one failure mode this refactor exists to remove.
 *
 * Every entry that counts takes `fmt` and asks CLDR for its plural form. None of them reads
 * the browser's locale, which is the present-tense bug F-2 describes: a Brazilian reading the
 * English build was shown "1.234 files" beside "1,234 people" on the same screen.
 *
 * ── What is NOT here ──
 *
 * Provider and surface names (`Microsoft`, `SharePoint`, `Amazon S3`) are proper nouns and
 * never translate, so they stay in `@ralph/contracts` and are interpolated here. `FILE_TICK_LIMIT`
 * and every other constant, and every function that CONSUMES these sentences, live beside the
 * components in `connected-folders.ts` (LOC-017, D-25): a validation limit that can drift per
 * locale is a bug rather than a translation, and a copy of one is worse.
 */

/**
 * The locale-bound formatters these sentences need, threaded in from a component's `useI18n()`.
 *
 * Written structurally rather than as `Pick<I18n, …>` on purpose. `I18n` carries `t: Messages`,
 * and `Messages` is `typeof en` — which includes this file, so picking from it here would make
 * the catalogue's own type depend on itself. A catalogue should not know the provider exists;
 * it should only know it is handed three functions, and `useI18n()` satisfies that structurally.
 */
export type FoldersFmt = {
  locale: Locale
  formatCount: (value: number) => string
  formatList: (items: readonly string[]) => string
}

/** Who can search what a folder loads, in the words the access block uses (PRD-26 D-38). */
export type FolderReaders =
  | { kind: 'private'; who: string }
  | { kind: 'groups'; names: string[]; people: number | null }
  | { kind: 'everyone' }

/** CLDR plural forms. `other` is required because every language has it; the rest are optional. */
type PluralForms = Partial<Record<Intl.LDMLPluralRule, string>> & { other: string }

/**
 * The plural form for a count, chosen by CLDR rather than by `count === 1`.
 *
 * Both shipped locales have two forms, which is exactly why the two-branch check is refused
 * rather than kept: `Intl.PluralRules('pt-BR').select(0)` returns `one`, so Portuguese takes
 * the SINGULAR at zero — "0 documento", not "0 documentos". A hand-rolled comparison is
 * therefore already wrong on every zero state in this file, in one of the two languages,
 * silently (LOC-015).
 *
 * The forms are written here at the entry and never passed in from a component, so a
 * translator finds every form of every noun in this one file. The three lines are repeated in
 * the Portuguese sibling rather than exported across, for the same reason: each locale file
 * stands on its own.
 */
const plural = (fmt: FoldersFmt, count: number, forms: PluralForms): string =>
  forms[pluralCategory(fmt.locale, count)] ?? forms.other

/** "1 file" / "2 files", counted and pluralised in the reader's language. */
const fileCount = (fmt: FoldersFmt, count: number): string =>
  `${fmt.formatCount(count)} ${plural(fmt, count, { one: 'file', other: 'files' })}`

/**
 * What a check did, as tally marks rather than as prose.
 *
 * Joined with a comma rather than `formatList`: these are readings in a status line, not a
 * sentence listing things, and "2 added, 1 changed and 3 withdrawn" reads as prose where a
 * reader wants a readout. Empty when nothing happened, which is what lets both callers print
 * "nothing had changed" instead — the quiet case said out loud (D-18).
 */
const tally = (fmt: FoldersFmt, added: number, changed: number, removed: number, withdrawn: number): string => {
  const parts: string[] = []
  if (added > 0) parts.push(`${fmt.formatCount(added)} added`)
  if (changed > 0) parts.push(`${fmt.formatCount(changed)} changed`)
  if (removed + withdrawn > 0) parts.push(`${fmt.formatCount(removed + withdrawn)} withdrawn`)
  return parts.join(', ')
}

/**
 * Why one file is not loaded, in the words the rows use.
 *
 * A map with `satisfies Record<ExclusionReason, string>` rather than the switch with a
 * `default: return ''` this replaced, and the difference is the whole point (LOC-018): the
 * switch covered all nine reasons today, but a tenth landing in contracts would have compiled,
 * shipped, and silently dropped the one line that explains why a file somebody is looking for
 * is missing. Now it is a compile error here instead — in each locale, separately.
 */
const EXCLUSION_LABEL = {
  unsupported: 'a format we cannot read',
  too_large: 'too large',
  too_old: 'left out by the date filter',
  type: 'left out by the type filter',
  rule: 'left out',
  not_selected: 'not chosen',
  removed_by_steward: 'removed by hand',
  folder: 'left out',
  base_full: 'the knowledge base is full'
} satisfies Record<ExclusionReason, string>

/**
 * What each place a folder can live actually is, one line each, beside its name.
 *
 * Exhaustive over the surface enum for the same reason the exclusion map is: a fifth surface
 * would otherwise render a glyph and a name with nothing under it, and nobody would notice
 * until somebody asked what the difference between two of them was.
 */
const SURFACE_HINT = {
  sharepoint: 'A document library on a site.',
  onedrive: 'A person’s own files.',
  drive: 'A folder in My Drive or a shared drive.',
  s3: 'A prefix in a bucket.'
} satisfies Record<ConnectorSurface, string>

/**
 * Said twice — once on its own in the folder detail, once after the provider's own names for
 * the formats in the preview — so it is written once here and read by both entries rather than
 * copied into each and left to drift.
 */
const UNSUPPORTED_BODY = 'They are counted and skipped; nothing about them changes anything else.'

export const knowledgeFolders = {
  tab: 'Connected folders',
  /** On the knowledge base's card, only when a folder needs a person. */
  baseNotice: (fmt: FoldersFmt, count: number) =>
    pluralCategory(fmt.locale, count) === 'one'
      ? 'A connected folder needs a person before it can sync.'
      : `${fmt.formatCount(count)} connected folders need a person before they can sync.`,

  empty: 'Nothing is being watched yet',
  emptyBody: (baseName: string) =>
    `A connected folder keeps this knowledge base current on its own. Files in a folder you choose are read into ${baseName}, and when someone adds, changes or removes one there, it follows. Nothing is ever written back.`,
  connect: 'Connect a folder',
  loading: 'Loading connected folders…',
  unavailable: 'The connected folders could not be loaded',
  unavailableBody: 'This is the platform, not the provider. Nothing has been disconnected.',

  // --- accounts --------------------------------------------------------------
  accounts: {
    title: 'Accounts',
    summary: (fmt: FoldersFmt, count: number) =>
      count === 0
        ? 'No accounts yet'
        : `${fmt.formatCount(count)} ${plural(fmt, count, { one: 'account', other: 'accounts' })}`,
    delegatedHint: (surface: string) =>
      `This platform will be able to read what this account can read in ${surface}, and only read. It never writes, moves, renames or deletes anything there.`,
    delegatedStops: (name: string) => `This folder syncs as ${name}; it stops when their account is deactivated.`,
    applicationHint:
      'A company account, set up by an administrator. It is not tied to any one person, so it survives someone leaving.',
    s3AdminOnly: 'An S3 bucket is read with keys — a company account — so an administrator connects it.',
    companyAdminOnly:
      'Company accounts — an S3 key, a Google service account, the organisation’s Microsoft app — are added and used by administrators.',
    readsThroughOwner: (owner: string) =>
      `This folder reads through ${owner}’s sign-in, so only they or an administrator can check it, change what it loads or resume it. Any maintainer can pause it, disconnect it, or move it onto their own sign-in.`,
    readsThroughCompany:
      'This folder reads through a company account, so only an administrator can check it, change what it loads or resume it. Any maintainer can pause it, disconnect it, or move it onto their own sign-in.',
    companyAccount: 'company account',
    show: 'show',
    hide: 'hide',
    keep: 'Keep',
    signedIn: (when: string) => `Signed in ${when}`,
    usedBy: (fmt: FoldersFmt, count: number) =>
      count === 0
        ? 'Not used by any folder'
        : `Used by ${fmt.formatCount(count)} ${plural(fmt, count, { one: 'folder', other: 'folders' })}`,
    signInWith: (provider: ConnectorProvider) => `Sign in with ${PROVIDER_LABEL[provider]}`,
    signInAgain: 'Sign in again',
    useKeys: 'Use these keys',
    remove: 'Remove',
    removeConfirm: (fmt: FoldersFmt, label: string, folders: number) =>
      folders === 0
        ? `Remove ${label}? Nothing is using it.`
        : `Remove ${label}? The ${fmt.formatCount(folders)} ${plural(fmt, folders, { one: 'folder', other: 'folders' })} using it will pause. Nothing is withdrawn.`,
    // The provider names are joined by `Intl.ListFormat` (LOC-016), which is also why English
    // gained its serial comma here: "SharePoint, OneDrive, and Google Drive" rather than the
    // three-way "and … and" the hand-rolled join produced.
    notConfigured: (fmt: FoldersFmt, missing: string[]) =>
      missing.length === 0
        ? ''
        : `Only ${plural(fmt, missing.length, { one: 'one provider is', other: 'some providers are' })} set up on this deployment — an administrator adds ${fmt.formatList(missing)}.`,
    whereFiles: 'Where the files are',
    /** Each surface, said as what it is rather than as what the provider calls its API. */
    surfaceHint: SURFACE_HINT,
    chooseAccount: 'Whose eyes to borrow',
    /**
     * One account in the list: the sign-in, the provider it belongs to, and — only for a
     * company account — that it is one. Composed here rather than at the call site because the
     * parenthetical is an apposition to the provider name, and a language that inflects it has
     * to see the whole line (D-20).
     */
    optionLabel: (account: string, provider: string, application: boolean) =>
      `${account} — ${provider}${application ? ' (application account)' : ''}`,
    differentAccount: (provider: ConnectorProvider) => `Sign in with a different ${PROVIDER_LABEL[provider]} account`,
    opensSignIn: (provider: ConnectorProvider) =>
      `Opens ${PROVIDER_LABEL[provider]}’s own sign-in page in this window.`,
    signInFailed: 'That sign-in did not finish.',
    /**
     * `provider` is the SUBJECT of the second sentence, so it is a catalogue entry rather than
     * a literal at the call site: the flow does not know which provider refused, and a language
     * that inflects around the subject cannot be handed an English noun phrase (D-20).
     */
    theProvider: 'The provider',
    signInFailedBody: (provider: string) =>
      `Nothing was connected and nothing here changed. If you meant to allow it, sign in again — ${provider} asks you to confirm what this platform may read before it hands anything over.`,
    change: 'Change',
    useServiceAccount: 'Use a service account instead',
    useApplicationAccount: 'Use an application account instead',
    checking: 'Checking…',
    connectAccount: 'Connect',
    tenantLabel: 'Tenant id',
    tenantHint:
      'The directory (tenant) id from the app registration an administrator consented to for the whole organisation.',
    serviceAccountLabel: 'Service account key',
    serviceAccountHint:
      'Paste the JSON key file from Google Cloud. Share the folders it should read with the service account’s email address; that is all the access it gets.',
    s3: {
      keyId: 'Access key id',
      secret: 'Secret access key',
      sessionToken: 'Session token (only for temporary keys)',
      region: 'Region',
      bucket: 'Bucket',
      endpoint: 'Endpoint (only for S3-compatible storage)',
      pathStyle: 'Path-style addressing (MinIO and most gateways)',
      policyTitle: 'The only permissions the key needs',
      policyHint:
        'Attach this to the key’s user or role before connecting. It reads the bucket’s object names (the first check needs that) and the contents of this prefix only. Nothing in it can write.',
      copy: 'Copy policy',
      copied: 'Copied'
    }
  },

  // --- finding the folder -------------------------------------------------------
  browse: {
    title: 'Where to watch',
    findSite: 'Find a site…',
    search: 'Search',
    siteHint: 'Sites you can open in SharePoint. Nothing you cannot already open is listed.',
    driveHint: 'What this account can open in Google Drive. Nothing it cannot already open is listed.',
    onedriveUser: 'Whose OneDrive',
    onedriveUserHint:
      'The person’s sign-in name, such as ana@acme.com. An application account reads any OneDrive in the organisation.',
    /** An example, not a name: it localizes with the rest of the placeholders (ADR-0018). */
    onedriveUserPlaceholder: 'ana@acme.com',
    openUser: 'Open',
    /** The breadcrumb of the levels already descended into. */
    whereYouAre: 'Where you are',
    open: (name: string) => `Open ${name}`,
    choose: (name: string) => `Choose ${name}`,
    /** The button's own word. Separate from `choose(name)` above, which is its accessible name. */
    chooseAction: 'Choose',
    chooseThis: 'Watch this folder',
    counting: 'Counting…',
    /** The listing's columns, read aloud only — the glyph and the name carry them on screen. */
    columns: {
      name: 'Name',
      contents: 'Contents',
      choose: 'Choose'
    },
    items: (fmt: FoldersFmt, count: number) =>
      `${fmt.formatCount(count)} ${plural(fmt, count, { one: 'item', other: 'items' })}`,
    filesHere: (fmt: FoldersFmt, count: number) =>
      count === 0
        ? 'no files in this folder'
        : `and ${fmt.formatCount(count)} ${plural(fmt, count, { one: 'file', other: 'files' })} in this folder`,
    nothingHere: 'Nothing in here that can be opened.',
    more: 'Show more',
    loading: 'Asking the provider…'
  },

  // --- the preview ------------------------------------------------------------
  preview: {
    title: 'What will be loaded',
    counting: (fmt: FoldersFmt, files: number) =>
      files === 0 ? 'Counting what is in here…' : `Still counting — ${fmt.formatCount(files)} files so far.`,
    summary: (fmt: FoldersFmt, readable: number, total: number, size: string, folders: number) =>
      readable === total
        ? `All ${fmt.formatCount(total)} files can be read — ${size} across ${fmt.formatCount(folders)} ${plural(fmt, folders, { one: 'folder', other: 'folders' })}.`
        : `${fmt.formatCount(readable)} of ${fmt.formatCount(total)} files can be read — ${size} across ${fmt.formatCount(folders)} ${plural(fmt, folders, { one: 'folder', other: 'folders' })}.`,
    nothingIncluded:
      'Nothing in this folder is included, so nothing from here can be cited. Tick a folder above, or clear the filters.',
    nothingReadable: (fmt: FoldersFmt, total: number, baseName: string) =>
      `None of the ${fmt.formatCount(total)} files here can be read. Connecting this folder would add nothing to ${baseName}.`,
    empty: 'This folder holds no files.',
    /**
     * The segmented bar's chips. Each owns its whole phrase INCLUDING the number, rather than
     * being a noun the component prefixes a count to: the number governs the agreement of the
     * words beside it in Portuguese ("1 será carregado" / "3 serão carregados"), so a map of
     * bare nouns is a phrase that is right in English and wrong in the other language at every
     * value but one (D-21).
     */
    legend: {
      included: (fmt: FoldersFmt, count: number) => `${fmt.formatCount(count)} will be loaded`,
      unsupported: (fmt: FoldersFmt, count: number) => `${fmt.formatCount(count)} in a format we cannot read`,
      tooLarge: (fmt: FoldersFmt, count: number) => `${fmt.formatCount(count)} too large`,
      filtered: (fmt: FoldersFmt, count: number) => `${fmt.formatCount(count)} left out by the filters`,
      leftOut: (fmt: FoldersFmt, count: number) => `${fmt.formatCount(count)} left out by you`
    },
    unsupportedHeading: (fmt: FoldersFmt, count: number) =>
      `${fmt.formatCount(count)} ${plural(fmt, count, { one: 'file is', other: 'files are' })} in a format we cannot read`,
    unsupportedBody: UNSUPPORTED_BODY,
    /**
     * The same sentence with the provider's own names for the formats in front of it.
     *
     * `unsupportedByKind` carries a free-text `label` written by the server ("a Word file") and
     * no code, so the client has nothing to key a label map off and the English branch has to
     * reshape that label to carry a count. That reshaping is English morphology, which LOC-014
     * forbids and which has no Portuguese rendering at all — so it lives HERE, inside the
     * English catalogue, where at least it is one locale's business and the Portuguese entry is
     * free to say the count differently. The real fix is a kind code on `PreviewSummary`; until
     * it lands, this is the smallest honest place for it.
     */
    unsupportedDetail: (kinds: ReadonlyArray<{ label: string; count: number }>) =>
      `${kinds
        .map((k) =>
          k.count === 1 ? k.label : `${k.count} ${k.label.replace(/^an? /, '').replace(/\bfile\b/, 'files')}`
        )
        .join(', ')}. ${UNSUPPORTED_BODY}`,
    tooLargeHeading: (fmt: FoldersFmt, count: number) =>
      `${fmt.formatCount(count)} ${plural(fmt, count, { one: 'file is', other: 'files are' })} too large`,
    tooLargeBody: (limit: string, largest: string | null) =>
      `A document may be up to ${limit}.${largest ? ` The largest here is ${largest}.` : ''}`,
    unknownSize: (fmt: FoldersFmt, count: number) =>
      `${fmt.formatCount(count)} ${plural(fmt, count, {
        one: 'is a Google Doc, Sheet or Slides deck',
        other: 'are Google Docs, Sheets or Slides decks'
      })}: exported when synced, so ${plural(fmt, count, { one: 'its', other: 'their' })} size is known then.`,
    listThem: 'List them',
    hideThem: 'Hide them',
    backToFolders: 'Back to the folders',
    /** The tail of a truncated list of files, under the last row. */
    andMore: (fmt: FoldersFmt, count: number) => `and ${fmt.formatCount(count)} more`,
    truncated: (fmt: FoldersFmt, cap: number) =>
      `This folder holds more than ${fmt.formatCount(cap)} files. That is more than one connected folder is meant to carry. The numbers here cover the first ${fmt.formatCount(cap)}. Choose a subfolder, or narrow by type or date, so what arrives is what you meant.`,
    otherKinds: (fmt: FoldersFmt, count: number, kinds: number) =>
      `${fmt.formatCount(count)} in ${fmt.formatCount(kinds)} other ${plural(fmt, kinds, { one: 'kind', other: 'kinds' })}`,
    /** The year is a moment, not a quantity, so it never takes a grouping separator. */
    suggestion: (fmt: FoldersFmt, count: number, year: number) =>
      `${fmt.formatCount(count)} files here have not changed since ${year}.`,
    leaveThemOut: 'Leave them out',
    willLoad: (fmt: FoldersFmt, files: number, size: string, leftOut: number) =>
      `${fmt.formatCount(files)} ${plural(fmt, files, { one: 'file', other: 'files' })} will be loaded now, ${size} in all.${leftOut > 0 ? ` ${fmt.formatCount(leftOut)} ${plural(fmt, leftOut, { one: 'is', other: 'are' })} left out and listed above.` : ''} From now on, anything added to this folder is loaded too, and anything removed there is withdrawn from here.`,
    willLoadCounting: (fmt: FoldersFmt, files: number) =>
      `Still counting — ${fmt.formatCount(files)} files so far. Starting now loads everything in this folder, including what the count has not reached yet. You can leave any of it out afterwards.`,
    start: 'Start syncing',
    startDisabledEmpty: 'Nothing here can be read, so there is nothing to start.',
    startDisabledNothingIncluded: 'Nothing is included yet, so there is nothing to start.',
    cancel: 'Cancel'
  },

  // --- selection --------------------------------------------------------------
  selection: {
    title: 'What is included',
    /** The breadcrumb above the level being ticked. */
    pathLabel: 'Folder path',
    /** The table's columns, read aloud only. */
    columns: {
      included: 'Included',
      name: 'Name',
      files: 'Files',
      size: 'Size'
    },
    include: (name: string, detail: string) => `Include ${name} — ${detail}`,
    /** What a ticked or unticked FILE says in its own row, where a folder gets a ratio instead. */
    includedMark: 'included',
    leftOutMark: 'left out',
    keptUpToDate: 'kept up to date — new files here are loaded too',
    leftOutFolder: 'left out — new files here are left out too',
    someOf: (fmt: FoldersFmt, included: number, total: number) =>
      `${fmt.formatCount(included)} of ${fmt.formatCount(total)} files`,
    allFilteredOut: (fmt: FoldersFmt, total: number) => `0 of ${fmt.formatCount(total)} files — none can be loaded`,
    files: (fmt: FoldersFmt, count: number) => fileCount(fmt, count),
    filesInFolder: (fmt: FoldersFmt, count: number, included: number) =>
      count === 0
        ? 'no files in this folder'
        : included === count
          ? `and ${fileCount(fmt, count)} in this folder — all included`
          : `and ${fileCount(fmt, count)} in this folder — ${fmt.formatCount(included)} included`,
    tooManyToTick:
      'Choose by folder, type or date here. Once the files are loaded you can leave any of them out one by one.',
    showMore: (fmt: FoldersFmt, shown: number, total: number) =>
      `Show ${fmt.formatCount(Math.min(100, total - shown))} more of ${fmt.formatCount(total)}`,
    skipList: 'Skip the folder list',
    filters: {
      allTypes: 'all types',
      // The chips state the setting; the consequence is the pending-changes sentence, which
      // the server computes — a number printed here as "leaves out N" would be a guess.
      onlyTypes: (types: string[]) => `only ${types.map((t) => t.replace(/^\./, '')).join(', ')}`,
      /** A file with no extension at all, in the type list. */
      noExtension: '(none)',
      anySize: 'any size',
      upTo: (size: string) => `up to ${size}`,
      anyTime: 'changed at any time',
      since: (date: string) => `changed since ${date}`,
      typesLegend: 'File types',
      sizeLegend: 'Largest file to load',
      sizeLabel: 'Megabytes',
      /** A unit symbol beside the field, not a word: it is the same in both languages. */
      sizeUnit: 'MB',
      sizeHint: (limit: string) => `A document may be up to ${limit} whatever you put here.`,
      dateLegend: 'Changed since',
      lastYear: 'last year',
      lastThreeYears: 'last 3 years',
      anyTimeChoice: 'any time',
      apply: 'Apply',
      clear: 'Clear'
    },
    pending: {
      region: 'Pending changes',
      working: 'Working out what changes…',
      both: (fmt: FoldersFmt, added: number, withdrawn: number) =>
        `${fmt.formatCount(added)} ${plural(fmt, added, { one: 'file', other: 'files' })} will be added. ${fmt.formatCount(withdrawn)} will be withdrawn from every future answer.`,
      addedOnly: (fmt: FoldersFmt, added: number) =>
        `${fmt.formatCount(added)} ${plural(fmt, added, { one: 'file', other: 'files' })} will be added.`,
      nothing: 'Nothing changes: what is included stays as it is.',
      body: 'Putting something back later loads it again from the provider. Citations given in the meantime say the document was withdrawn.',
      apply: 'Apply these changes',
      applying: 'Applying…',
      applyLabel: (fmt: FoldersFmt, added: number, withdrawn: number) =>
        `Apply these changes — ${fmt.formatCount(added)} added, ${fmt.formatCount(withdrawn)} withdrawn`,
      discard: 'Discard',
      applied: (fmt: FoldersFmt, added: number, withdrawn: number) =>
        added === 0 && withdrawn === 0
          ? 'Applied. Nothing needed to change.'
          : `Applied. ${fmt.formatCount(added)} to add, ${fmt.formatCount(withdrawn)} withdrawn.`,
      unsaved: (fmt: FoldersFmt, added: number, withdrawn: number) =>
        `You have ${fmt.formatCount(added)} files to add and ${fmt.formatCount(withdrawn)} to withdraw that have not been applied.`,
      keepEditing: 'Keep editing',
      discardAndClose: 'Discard and close',
      applyAndClose: 'Apply and close'
    }
  },

  // --- the access boundary (PRD-25 D-8, PRD-26 D-38) ------------------------------
  access: {
    you: 'you',
    groupManagers: (group: string) => `${group}’s managers`,
    headline: (readers: FolderReaders, baseName: string): string => {
      switch (readers.kind) {
        case 'private':
          return `Only ${readers.who} will be able to search what you include here.`
        case 'groups':
          return `Everyone who can use ${baseName} will be able to search everything you include here.`
        case 'everyone':
          return 'Everyone who signs in will be able to search everything you include here.'
      }
    },
    groupsAre: (fmt: FoldersFmt, names: string[], people: number | null) =>
      `That is everyone in ${fmt.formatList(names)}${people !== null ? ` — ${fmt.formatCount(people)} ${plural(fmt, people, { one: 'person', other: 'people' })}` : ''}.`,
    provider: (surface: string, baseName: string) =>
      `It does not matter who can open these files in ${surface}: once a file is loaded, anyone who can use ${baseName} can find it in an answer.`,
    privateLater: (baseName: string) =>
      `If ${baseName} is shared more widely later, this folder asks you to confirm again before it loads anything more.`,
    restrictedWayOut:
      'If some of these files should reach fewer people, connect them to a knowledge base shared more narrowly instead.',
    everyoneBody: (baseName: string, surface: string) =>
      `${baseName} is shared with everyone. Files in this folder that only a few people can open in ${surface} will be readable by all of them once they are loaded.`,
    everyoneAck: 'I understand that everyone who signs in will be able to search these files.',
    everyoneWayOut: (baseName: string) =>
      `If that is not what you want, share ${baseName} more narrowly first, or connect this folder to a knowledge base that already is.`,
    agents: (fmt: FoldersFmt, count: number, baseName: string) =>
      pluralCategory(fmt.locale, count) === 'one'
        ? `An agent also answers from ${baseName} for everyone it reaches, including people who cannot use ${baseName}.`
        : `${fmt.formatCount(count)} agents also answer from ${baseName} for everyone they reach, including people who cannot use ${baseName}.`,
    widened: (baseName: string) =>
      `Who can reach ${baseName} changed after this folder was connected — it was shared more widely, moved, or an agent was allowed to answer from it for everyone it reaches. Confirm who can search what this folder loads to keep syncing.`,
    acknowledge: 'Confirm and keep syncing',
    readBy: (fmt: FoldersFmt, readers: FolderReaders): string => {
      switch (readers.kind) {
        case 'private':
          return `only ${readers.who}`
        case 'groups':
          return `everyone in ${fmt.formatList(readers.names)}${readers.people !== null ? ` (${fmt.formatCount(readers.people)} ${plural(fmt, readers.people, { one: 'person', other: 'people' })})` : ''}`
        case 'everyone':
          return 'everyone who signs in'
      }
    }
  },

  // --- the folder row and its standing ---------------------------------------------
  standing: {
    watching: 'Watching',
    dueNow: 'any moment now',
    countAgain: 'Count again',
    signInWithMine: 'Sign in with my account',
    loading: (fmt: FoldersFmt, done: number, total: number) =>
      `Loading ${fmt.formatCount(done)} of ${fmt.formatCount(total)}…`,
    counting: 'Counting what is in here…',
    countingSoFar: (fmt: FoldersFmt, files: number) => `${fmt.formatCount(files)} files so far`,
    notStarted: 'Not started',
    notStartedHint: 'Choose what to load',
    notStartedRow: 'Counted and waiting. Open it to choose what to load.',
    paused: 'Paused',
    pausedHint: 'Nothing new is arriving. What is here stays and keeps answering.',
    needsSignIn: 'Needs signing in again',
    ownerMustSignIn: (owner: string) => `${owner} needs to sign in again; only they can renew this account.`,
    needsSignInHint: (since: string | null) =>
      since
        ? `Nothing new has arrived since ${since}. What was loaded stays and keeps answering.`
        : 'Nothing new is arriving. What was loaded stays and keeps answering.',
    failed: 'Last check failed',
    slowed: (surface: string) => `${surface} is asking us to slow down`,
    slowedHint: (when: string | null) =>
      `${when ? `Next try ${when}.` : 'It will be tried again shortly.'} Nothing is lost.`,
    ownerLeft: (name: string) => `${name} signed this account in, and their account has been closed.`,
    ownerLeftBody: () =>
      'The folders using it have stopped; nothing has been withdrawn. Any of the base’s maintainers can move them onto their own sign-in, or an administrator onto another account.',
    checked: (when: string) => `checked ${when}`,
    next: (when: string) => `next ${when}`,
    included: (fmt: FoldersFmt, included: number, total: number) =>
      `${fmt.formatCount(included)} of ${fmt.formatCount(total)} files included`,
    rootGone: (surface: string, name: string) => `${name} is no longer in ${surface}.`,
    rootGoneBody: (fmt: FoldersFmt, docs: number) =>
      `Syncing has stopped and nothing has been withdrawn — the ${fmt.formatCount(docs)} ${plural(fmt, docs, { one: 'document', other: 'documents' })} already here ${plural(fmt, docs, { one: 'is', other: 'are' })} still answering. If the folder was moved, connect the new one and then disconnect this. If it was deleted on purpose, disconnect this folder and choose what happens to the documents.`,
    forbidden: (name: string) => `This account can no longer open ${name}.`,
    forbiddenBody: (fmt: FoldersFmt, docs: number, account: string) =>
      `Syncing has stopped and nothing has been withdrawn — the ${fmt.formatCount(docs)} ${plural(fmt, docs, { one: 'document', other: 'documents' })} already here ${plural(fmt, docs, { one: 'is', other: 'are' })} still answering. Ask whoever manages that folder to give ${account} read access, then check again. If access was removed on purpose, disconnect this folder and choose what happens to the documents.`,
    emptyListing: (name: string) => `${name} came back empty.`,
    emptyListingBody:
      'Where there were files, the last check found none. Nothing has been withdrawn. Check again — or, if the folder was emptied on purpose, disconnect it and choose what happens to the documents.',
    interrupted: 'The last check was interrupted before it finished. Nothing was withdrawn; it will run again.',
    checkAgain: 'Check again',
    open: 'Open'
  },

  // --- the folder detail --------------------------------------------------------
  detail: {
    /** The drawer's accessible name: the path, and what kind of thing it is. */
    drawerLabel: (path: string) => `${path} — connected folder`,
    where: 'Where it watches',
    account: 'account',
    readBy: 'read by',
    lastChecked: 'last checked',
    nextCheck: 'next check',
    loaded: 'loaded',
    /** The value beside "loaded" and inside the progress meter: a ratio, so it carries no noun. */
    loadedOf: (fmt: FoldersFmt, done: number, total: number) => `${fmt.formatCount(done)} of ${fmt.formatCount(total)}`,
    problems: 'Not loaded',
    leftOutByYou: 'Left out by you',
    leftOutByYouBody:
      'Files you removed by hand, and folders you unticked. They are not loaded, and new files inside an unticked folder are not loaded either.',
    /** A whole folder in the left-out list, with what is inside it. */
    leftOutFolderKind: (fmt: FoldersFmt, files: number) => `folder, ${fileCount(fmt, files)}`,
    putBack: 'Put back',
    nothingLeftOut: 'Nothing has been left out by hand.',
    orphanRules: (fmt: FoldersFmt, count: number) =>
      `${fmt.formatCount(count)} earlier ${plural(fmt, count, { one: 'choice', other: 'choices' })} no longer ${plural(fmt, count, { one: 'applies', other: 'apply' })} — the files they named are no longer in the folder.`,
    history: 'History',
    noHistory: 'No checks yet.',
    nothingChanged: 'nothing had changed',
    /** A check still running, mid-line in the history list — never the button's "Checking…". */
    checkingRow: 'checking…',
    stoppedSignIn: 'stopped — signing in again was needed',
    stopped: (reason: string) => `stopped — ${reason}`,
    /** When the server gave no reason at all. It is still a reason, so it reads like one. */
    stoppedUnknown: 'it did not finish',
    /** Who asked for a check, when it was not the schedule. The name is theirs and never translates. */
    triggeredBy: (who: string) => `by ${who}`,
    counted: (fmt: FoldersFmt, files: number) => `counted ${fmt.formatCount(files)} files`,
    /** What one check did, as a readout: "2 added, 1 changed, 3 withdrawn", or empty when nothing did. */
    syncTally: (fmt: FoldersFmt, added: number, changed: number, removed: number, withdrawn: number) =>
      tally(fmt, added, changed, removed, withdrawn),
    checkNow: 'Check now',
    checking: 'Checking…',
    checkedResult: (fmt: FoldersFmt, added: number, changed: number, removed: number, withdrawn: number) => {
      const parts = tally(fmt, added, changed, removed, withdrawn)
      return parts === '' ? 'Checked just now. Nothing had changed.' : `Checked just now. ${parts}.`
    },
    checkQueued: 'Checking in the background — the row updates as it finishes.',
    pause: 'Pause',
    pauseConfirm: (fmt: FoldersFmt, name: string, docs: number) =>
      `Pause ${name}? Nothing new arrives and nothing is withdrawn. The ${fmt.formatCount(docs)} ${plural(fmt, docs, { one: 'document', other: 'documents' })} already here ${plural(fmt, docs, { one: 'keeps', other: 'keep' })} answering. Resuming picks up every change made while it was paused.`,
    resume: 'Resume',
    howOften: 'How often to check',
    interval: {
      soon: 'As soon as we can — about every 30 minutes',
      soonHint: (surface: string) => `Changes in ${surface} show up in answers within the hour.`,
      fewTimes: 'A few times a day',
      fewTimesHint: (surface: string) => `Gentler on ${surface}. Fine for policies and handbooks.`,
      daily: 'Once a day',
      dailyHint: 'For folders that change rarely.'
    },
    disconnect: 'Disconnect…',
    disconnectTitle: (name: string) => `Disconnect ${name}?`,
    disconnectBody: (fmt: FoldersFmt, surface: string, docs: number) =>
      `Nothing new will arrive from ${surface}. Choose what happens to the ${fmt.formatCount(docs)} ${plural(fmt, docs, { one: 'document', other: 'documents' })} already here.`,
    keepThem: 'Keep them',
    keepThemHint: (baseName: string, surface: string) =>
      `They stay in ${baseName} and stop being kept up to date. Their links back to ${surface} still work.`,
    withdrawThem: 'Withdraw them',
    withdrawThemHint:
      'They come out of every future answer. Citations already given keep working and say the document was withdrawn.',
    confirmDisconnect: 'Disconnect',
    keepSyncing: 'Keep syncing',
    retry: 'Try these again',
    retryHint: (surface: string) =>
      `${surface} refused them. This is usually temporary and they are tried again at the next check.`,
    failedHeading: (fmt: FoldersFmt, count: number) =>
      `${fmt.formatCount(count)} ${plural(fmt, count, { one: 'file', other: 'files' })} could not be fetched`,
    twoCopies:
      'The same file in two connected folders is loaded twice, once for each. Taking it out of one leaves the other answering.',
    rebind: 'Use a different account',
    rebindHint: 'The folder keeps everything it loaded; nothing is fetched again.',
    /** The row for the account the folder already reads through, beside the ones it could move to. */
    currentAccount: 'the account this folder uses now',
    close: 'Close'
  },

  // --- synced documents in the table and drawer ---------------------------------
  document: {
    fromFolder: 'from a connected folder',
    where: 'Where it came from',
    folder: 'folder',
    account: 'account',
    changed: 'changed',
    hereSince: 'here since',
    openIn: (surface: string) => `Open in ${surface}`,
    opensNewTab: (surface: string) => `opens ${surface} in a new tab`,
    /**
     * The same two sentences for a document whose provider was never recorded — and a pair of
     * entries rather than `openIn('a connected folder')`, which is what this used to be.
     *
     * English cannot tell the difference: "Open in SharePoint" and "Open in a connected folder"
     * take the same preposition. Portuguese cannot say both from one entry — a provider name
     * takes a contraction ("no SharePoint") and a common noun takes its own article ("na pasta
     * conectada") — so passing the noun phrase in as a parameter is a sentence that is right in
     * one language only (D-21). Two entries, one per case, is the shape that survives.
     */
    openAtSource: 'Open in a connected folder',
    opensNewTabAtSource: 'opens a connected folder in a new tab',
    fetchAgain: 'Fetch it again',
    fetching: 'Fetching…',
    removeFooter: (path: string) =>
      `Removing takes its passages out of every future answer and leaves it out of ${path} from now on. You can put it back from the folder.`,
    removeAndLeaveOut: 'Remove and leave it out',
    /**
     * The count crosses into this entry rather than a pre-built subject phrase, because the
     * SUBJECT and its VERB have to agree and they are one sentence (D-20).
     *
     * It used to take a `passages` fragment built by the caller, which is how this shipped
     * reading "Its 1 passage come out" — the caller fixed the noun and the verb stayed plural
     * here, each half correct in its own file and wrong on the screen. A fragment passed into
     * a sentence whose grammar depends on it is the shape this PRD exists to remove; in
     * Portuguese it does not even have a correct answer, since the participle would have to
     * agree with a noun this entry never sees.
     */
    removeConfirm: (fmt: FoldersFmt, title: string, surface: string, path: string, passages: number) => {
      const consequence =
        passages === 0
          ? 'Nothing it contributed is in any future answer'
          : pluralCategory(fmt.locale, passages) === 'one'
            ? `Its ${fmt.formatCount(passages)} passage comes out of every future answer`
            : `Its ${fmt.formatCount(passages)} passages come out of every future answer`
      return `Remove “${title}”? It came from ${surface} · ${path}. ${consequence}, and it is left out of that folder from now on, so the next check will not bring it back. You can put it back from the folder at any time. Citations already given keep working and say it was withdrawn.`
    }
  },

  /** `detail` is the provider's own name for the format ("a Keynote deck"), so it wins when present. */
  reason: (reason: ExclusionReason | null, detail: string | null): string => {
    if (reason === null) return ''
    if (reason === 'unsupported' && detail) return `${detail} — cannot be read`
    return EXCLUSION_LABEL[reason]
  }
}

/*
 * Nine entries were deleted here rather than translated, following the convention
 * `marketplace/strings.ts` documents and PRD-27 D-27.
 *
 * `browse.back`, `preview.legend.readable`, `detail.checkMissing`, `detail.disconnected`,
 * `detail.putAllBack`, `selection.everythingIn`, `selection.pending.withdrawnOnly`,
 * `accounts.connected` and `document.from` had no render site left anywhere in the app. Some
 * are the residue of controls that moved — the disconnect outcome is chosen before the call
 * now, so there is no sentence afterwards; the legend chip for "readable" was folded into the
 * summary sentence — and some never had one. Translating a string nobody reads costs a
 * reviewer's attention twice, once per language, and buys a second copy to keep in step.
 */
